import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { CommercialDownloadButton } from "./DownloadButton";
import { useDeleteInvoice } from "@/lib/commercialQueries";
import { SimpleDeleteConfirm } from "../shared/SimpleDeleteConfirm";

/**
 * Master v5 Prompt 14 — InvoiceRecord/receipt list + authorized
 * download. `record_type` covers both invoices and receipts. Prompt
 * 30D — a coach (never a client) can permanently delete a record
 * (never touches the underlying downloadable document FileAsset).
 */
export function InvoicesCard({ workspaceId, invoices, isClientActor }) {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const deleteInvoice = useDeleteInvoice(workspaceId);

  async function handleConfirmDelete() {
    try {
      await deleteInvoice.mutateAsync(deleteTarget.inv_id);
      toast.success("Invoice record deleted");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't delete that invoice");
    }
  }

  return (
    <div className="border border-ink/10 rounded-lg p-4 bg-paper" data-testid="commercial-invoices-card">
      <h2 className="font-serif text-lg text-ink mb-2">Invoices &amp; Receipts</h2>
      {invoices.length === 0 ? (
        <p className="text-sm text-ink-soft italic" data-testid="commercial-invoices-empty">No invoices or receipts yet.</p>
      ) : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <div key={inv.inv_id} className="flex items-center justify-between gap-2" data-testid={`commercial-invoice-row-${inv.inv_id}`}>
              <div className="text-sm min-w-0">
                <p className="text-ink capitalize font-medium">{inv.record_type} · {inv.currency} {inv.amount.toLocaleString()}</p>
                <p className="text-ink-soft text-xs">{new Date(inv.issued_at).toLocaleDateString()} · <span className="capitalize">{inv.status}</span></p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <CommercialDownloadButton workspaceId={workspaceId} fasId={inv.document_fas_id} testId={`commercial-invoice-download-${inv.inv_id}`} />
                {!isClientActor && (
                  <button onClick={() => setDeleteTarget(inv)} title="Delete permanently" data-testid={`commercial-invoice-delete-${inv.inv_id}`}>
                    <Trash2 size={13} className="text-ink-soft hover:text-red-600" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <SimpleDeleteConfirm
        open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this invoice record permanently?"
        description="The downloadable document itself is not affected — only this record. This can't be undone."
        onConfirm={handleConfirmDelete} isPending={deleteInvoice.isPending}
        testId="commercial-invoice-delete-confirm"
      />
    </div>
  );
}
